import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTrialPatients } from "@/hooks/useActiveTrials";
import { useBillableEventTrigger } from "@/hooks/useBillableEventTrigger";
import { useAutoBillingTrigger } from "@/hooks/useAutoBillingTrigger";
import { Users, UserPlus, Calendar, BarChart3 } from "lucide-react";
import { AssignPatientToTrialDialog } from "./AssignPatientToTrialDialog";
import { PatientVisitsDisplay } from "./PatientVisitsDisplay";
import { TrialFlowPipeline } from "./TrialFlowPipeline";
import { TooltipHelp } from "@/components/ui/tooltip-help";

interface TrialPatientManagerProps {
  trialId: string;
  trialSiteId: string;
}

export function TrialPatientManager({ trialId, trialSiteId }: TrialPatientManagerProps) {
  const { data: patients = [], updatePatientStatus, isUpdating } = useTrialPatients(trialId);
  const { triggerBillableEvent } = useBillableEventTrigger();
  const { triggerAutoBilling } = useAutoBillingTrigger();
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'screening': return 'bg-yellow-100 text-yellow-800';
      case 'enrolled': return 'bg-blue-100 text-blue-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'withdrawn': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStatusChange = async (patientStatusId: string, patientId: string, newStatus: string) => {
    setUpdatingId(patientStatusId);
    
    updatePatientStatus({ id: patientStatusId, status: newStatus }, {
      onSuccess: async () => {
        // Map status changes to milestone events
        const milestoneMap: Record<string, string> = {
          'enrolled': 'enrollment_milestone',
          'active': 'randomization_milestone',
          'completed': 'completion_milestone',
        };

        const eventTrigger = milestoneMap[newStatus];
        
        if (eventTrigger) {
          // Use new auto-billing system
          await triggerAutoBilling({
            trial_id: trialId,
            trial_site_id: trialSiteId,
            patient_id: patientId,
            event_trigger_type: eventTrigger,
            notes: `Patient status changed to ${newStatus}`,
          });
        }
        
        setUpdatingId(null);
      },
      onError: () => {
        setUpdatingId(null);
      }
    });
  };

  const enrolledCount = patients.filter(p => ['enrolled', 'active'].includes(p.status)).length;

  return (
    <>
      <Tabs defaultValue="patients" className="space-y-4">
        <TabsList>
          <TabsTrigger value="patients" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Patients
            <Badge variant="secondary" className="ml-1">{enrolledCount}</Badge>
          </TabsTrigger>
          <TabsTrigger value="visits" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Scheduled Visits
          </TabsTrigger>
          <TabsTrigger value="pipeline" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Pipeline
          </TabsTrigger>
        </TabsList>

        <TabsContent value="patients">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Trial Patients
                  <Badge variant="secondary">{enrolledCount} Enrolled</Badge>
                  <TooltipHelp 
                    type="help"
                    content="Patients assigned to this trial. Change patient status using the dropdown - billing events are automatically generated for enrolled, active (randomized), and completed milestones."
                  />
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => setShowAssignDialog(true)}>
                  <UserPlus className="h-4 w-4 mr-1" />
                  Assign Patient
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {patients.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No patients assigned to this trial yet. Click "Assign Patient" to add patients.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Patient Number</TableHead>
                        <TableHead>Patient Name</TableHead>
                        <TableHead className="flex items-center gap-1">
                          Status
                          <TooltipHelp 
                            type="info"
                            size="sm"
                            content="Enrolled → Auto-bills enrollment milestone. Active → Auto-bills randomization. Completed → Auto-bills completion milestone."
                          />
                        </TableHead>
                        <TableHead>Status Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {patients.map((patient) => (
                        <TableRow key={patient.id}>
                          <TableCell className="font-mono text-sm">
                            {patient.patient?.patient_number || 'N/A'}
                          </TableCell>
                          <TableCell>{patient.patient?.full_name || 'Unknown'}</TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(patient.status)}>
                              {patient.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {patient.status_date 
                              ? new Date(patient.status_date).toLocaleDateString()
                              : '-'
                            }
                          </TableCell>
                          <TableCell>
                            <Select
                              value={patient.status}
                              onValueChange={(value) => handleStatusChange(patient.id, patient.patient_id, value)}
                              disabled={isUpdating && updatingId === patient.id}
                            >
                              <SelectTrigger className="w-[140px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="screening">Screening</SelectItem>
                                <SelectItem value="enrolled">Enrolled</SelectItem>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="withdrawn">Withdrawn</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="visits">
          <PatientVisitsDisplay trialId={trialId} trialSiteId={trialSiteId} />
        </TabsContent>

        <TabsContent value="pipeline">
          <TrialFlowPipeline trialSiteId={trialSiteId} trialId={trialId} />
        </TabsContent>
      </Tabs>
    
      <AssignPatientToTrialDialog
        open={showAssignDialog}
        onOpenChange={setShowAssignDialog}
        trialId={trialId}
        trialSiteId={trialSiteId}
      />
    </>
  );
}
